import { useState } from 'react'
import { Alert, Button, Form, Input } from 'antd'
import { Link } from 'react-router-dom'
import { forgotPassword } from '@/features/auth/api'
import { authLog } from '@/features/auth/debug'

type ForgotPasswordFormProps = {
  initialEmail?: string
  initialInfo?: string | null
}

export default function ForgotPasswordForm({ initialEmail = '', initialInfo = null }: ForgotPasswordFormProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(initialInfo)

  const onFinish = async (values: { email: string }) => {
    setLoading(true)
    setError(null)
    setInfo(null)
    authLog('Forgot password submit', { email: values.email })

    try {
      const response = await forgotPassword(values.email)
      authLog('Forgot password response received', { email: values.email, response })

      if (response.message === 'UserLockedOut' || response.code === 'UserLockedOut') {
        setError('Your account has been locked out. Please contact support for assistance.')
        return
      }

      if (response.error) {
        setError(response.error)
        return
      }

      setInfo(
        response.message ??
          "You should receive an email shortly. Please follow the instruction in the email to reset your password.",
      )
    } catch (e) {
      authLog('Forgot password flow threw error', e)
      setError(e instanceof Error ? e.message : 'Unable to process forgot password request')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {error && <Alert className="mb-4" type="error" showIcon message={error} />}
      {info && <Alert className="mb-4" type="success" showIcon message={info} />}

      <Form layout="vertical" onFinish={onFinish} initialValues={{ email: initialEmail }}>
        <Form.Item
          label="Email"
          name="email"
          rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Invalid email' }]}
        >
          <Input autoComplete="email" />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={loading} className="w-full">
          Submit
        </Button>
      </Form>

      <div className="mt-4 text-center">
        <Link className="text-brand-700 hover:text-brand-800 text-sm font-medium" to="/login">
          Back to login
        </Link>
      </div>
    </>
  )
}
